import { useState, useRef } from 'react';
import { B } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { collectEntriesFromInputFiles } from '../utils/docFolders';
import { useDocumentFolders } from '../hooks/useDocumentFolders';
import { FolderPicker } from '../components/shared/FolderPicker';
import { openDocument, isPdf, isOffice } from '../utils/docViewer';
import { downloadDocumentsZip, describeBundle } from '../utils/exportDownload';
import { deleteInChunks } from '../utils/bulkDelete';
import { DocViewerModal } from '../components/shared/DocViewerModal';
import { Btn, Input, Select, Modal, Spinner, ErrorMsg, Card, useToast, Icon, clickable } from '../components/ui';

const InfoGrid = ({ items, cols = 2 }) => (
  <div className="harvus-responsive-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, marginBottom: 20 }}>
    {items.filter(Boolean).map(([label, value]) => (
      <div key={label} style={{ background: B.surfaceMuted, borderRadius: 6, padding: "10px 14px" }}>
        <div style={{ fontSize: 11, color: B.textLt, marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{value ?? "—"}</div>
      </div>
    ))}
  </div>
);

// "Download all" for a tree node (a deal, one of its details, or a folder).
// Stops the click from toggling the node it sits on.
const BundleBtn = ({ label, busy, disabled, onClick, tone = 'dark' }) => (
  <button
    onClick={e => { e.stopPropagation(); onClick(); }}
    disabled={busy || disabled}
    title={disabled ? 'Nothing to download' : label}
    aria-label={label}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none',
      border: `1px solid ${tone === 'dark' ? 'rgba(255,255,255,0.35)' : B.border}`,
      borderRadius: 5, padding: '2px 7px', marginLeft: 8,
      fontSize: 10.5, fontWeight: 600, fontFamily: 'inherit',
      color: tone === 'dark' ? '#fff' : B.navy,
      opacity: (busy || disabled) ? 0.5 : 1,
      cursor: (busy || disabled) ? 'default' : 'pointer',
    }}>
    <Icon name="archive" size={11} /> {busy ? 'Preparing…' : 'Zip'}
  </button>
);

// variant="ghost" defaults to a white background — overriding only `color`
// (as the selection toolbar below used to) leaves white-on-white, invisible
// on the navy bar. Override background + border too, matching the ghost-on-
// dark convention used elsewhere (e.g. RealEstateBudgetTab.jsx).
const toolbarBtnStyle = { background: 'rgba(255,255,255,.08)', color: '#fff', border: '1px solid rgba(255,255,255,.35)' };

// Tick box for bulk selection. Stops the click from toggling the tree node it
// sits on; `partial` is the deal/folder row state when only some files under
// it are ticked.
const Tick = ({ checked, partial = false, onChange, label, tone = 'light' }) => (
  <input type="checkbox" checked={checked} aria-label={label} title={label}
    ref={el => { if (el) el.indeterminate = !checked && partial; }}
    onClick={e => e.stopPropagation()}
    onChange={onChange}
    style={{ width: 14, height: 14, margin: 0, cursor: 'pointer', flexShrink: 0,
      accentColor: tone === 'dark' ? B.teal : B.navy }} />
);

// Status cell: ↑ uploaded, ✕ failed (with retry), OCR indexed
const StatusCell = ({ st, row, retryingOcr, retryOcr }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
    {st === 'ocr'      && <span style={{ fontSize: 11, fontWeight: 700, color: B.green, letterSpacing: 0.3 }}>OCR</span>}
    {st === 'uploaded' && <span title="Uploaded" style={{ display: 'flex', color: B.green, lineHeight: 1 }}><Icon name="check" size={15} /></span>}
    {st === 'failed'   && (
      <>
        <span
          title={row.ocrError ? `OCR failed: ${row.ocrError}` : 'OCR failed'}
          style={{ display: 'flex', color: B.red, cursor: 'help' }}><Icon name="warning" size={14} /></span>
        <button onClick={() => retryOcr(row.documentID)} disabled={retryingOcr.has(row.documentID)}
          aria-label="Retry OCR"
          title={`Retry OCR${row.ocrError ? ': ' + row.ocrError : ''}`}
          style={{ background: "none", border: "none", cursor: "pointer", display: "inline-flex", color: B.textMd, padding: "0 2px", opacity: 0.8 }}>
          {retryingOcr.has(row.documentID) ? "…" : <Icon name="refresh" size={13} />}
        </button>
      </>
    )}
  </div>
);

// Actions cell: View, Download, Delete
const DocActions = ({ row, viewing, setViewing, downloading, download, deleting, setDeleting, openPreview, reload, api, setViewerState, onDelete }) => {
  const { toast } = useToast();
  return (
  <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "flex-end" }}>
    <Btn size="sm" variant="ghost" disabled={viewing === row.documentID}
      onClick={() => {
        if (isPdf(row.fileName) || isOffice(row.fileName)) {
          setViewing(row.documentID);
          openDocument(row, api, setViewerState, toast.error).finally(() => setViewing(null));
        } else { openPreview(row); }
      }}>
      {viewing === row.documentID ? "…" : "View"}
    </Btn>
    <Btn size="sm" variant="secondary" aria-label="Download document" disabled={downloading === row.documentID} onClick={() => download(row)}>
      {downloading === row.documentID ? "…" : <Icon name="download" size={14} />}
    </Btn>
    <button disabled={deleting === row.documentID}
      aria-label="Delete document"
      onClick={() => onDelete(row)}
      style={{ background: "none", border: "none", cursor: "pointer", color: B.red, display: "inline-flex", padding: "0 2px", opacity: 0.7 }}>
      {deleting === row.documentID ? "…" : <Icon name="trash" size={14} />}
    </button>
  </div>
  );
};

export const Documents = () => {
  const { toast } = useToast();
  const [searchInput,     setSearchInput]     = useState("");
  const [search,          setSearch]          = useState("");
  const [dealFilter,      setDealFilter]      = useState("");   // dealID, or '' for every deal
  const [selected,        setSelected]        = useState(() => new Set()); // documentIDs ticked for bulk delete
  const [bulkDeleting,    setBulkDeleting]    = useState(false);
  const [preview,         setPreview]         = useState(null);
  const [collapsedFolders,setCollapsedFolders]= useState(new Set());
  const [retryingOcr,     setRetryingOcr]     = useState(new Set());
  const [viewerState,  setViewerState]  = useState(null);
  const [viewing,      setViewing]      = useState(null);
  const [downloading,  setDownloading]  = useState(null);
  const [deleting,     setDeleting]     = useState(null);
  const [confirmDel,   setConfirmDel]   = useState(null);
  const [bundling,     setBundling]     = useState(null); // tree key whose zip is building
  const [showUpload,       setShowUpload]       = useState(false);
  const [uploadFiles,      setUploadFiles]      = useState(null); // File[] | null
  const [uploadDealId,     setUploadDealId]     = useState('');
  const [uploadLinkedItem, setUploadLinkedItem] = useState(null); // { type, id, label }
  const [uploadDescription,setUploadDescription]= useState('');
  const [uploadFolder,     setUploadFolder]     = useState('');
  const [uploadTaxYear,    setUploadTaxYear]    = useState('');
  const [uploadingDoc,     setUploadingDoc]     = useState(false);
  const [uploadErr,        setUploadErr]        = useState(null);
  const uploadFileRef   = useRef();
  const uploadFolderRef = useRef();
  const searchTimer                    = useRef(null);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(val), 400);
  };

  // Both endpoints already narrow by deal server-side, so the filter rides on
  // the request rather than being applied to a whole-book list in the browser.
  const dealQs = dealFilter ? `dealId=${encodeURIComponent(dealFilter)}` : '';
  const { data: docs, loading, error, reload } = useApi(
    () => search
      ? api.get(`document-search?q=${encodeURIComponent(search)}${dealQs ? '&' + dealQs : ''}`, { timeout: 30000 })
      : api.get(`documents${dealQs ? '?' + dealQs : ''}`, { timeout: 30000 }),
    [search, dealFilter]
  );
  const { data: allDeals }   = useApi(() => api.get('deals', { timeout: 30000 }), []);
  const [uploadDealDetails, setUploadDealDetails] = useState([]);

  const openPreview = async (row) => { setPreview(row); };

  const download = async (row) => {
    setDownloading(row.documentID);
    try {
      const { sasUrl, fileName } = await api.get(`documents/${row.documentID}/download-url`);
      const a = document.createElement('a');
      a.href = sasUrl; a.download = fileName; a.click();
    } catch (e) { toast.error(e.message); }
    finally { setDownloading(null); }
  };

  const filtered = docs || [];

  // EMPTY FOLDERS. The tree below is built from documents, so a folder with
  // nothing in it yet — including one just made from the upload modal — never
  // appeared, and read as a folder that had not been created. With a deal
  // filtered, that deal's live folder list is folded in; a folder created here
  // is added straight away whatever the filter.
  const { folders: filterDealFolders } = useDocumentFolders({
    dealId: dealFilter || null, enabled: !!dealFilter && !search,
  });
  const [createdFolders, setCreatedFolders] = useState([]); // [{ dealId, path }]

  // Build 3-level tree: deal → dealDetail (optional) → folder → files
  const docTree = (() => {
    const dealMap = Object.fromEntries((allDeals || []).map(d => [d.dealID, d]));
    const byDeal  = {};

    for (const doc of filtered) {
      const dealId   = doc.dealID    || null;
      const detailId = doc.dealDetailID || null;
      const dealName = dealId ? (dealMap[dealId]?.name || 'Unknown Deal') : '— General —';

      const dk = dealId || '__general__';
      if (!byDeal[dk]) byDeal[dk] = { label: dealName, key: `d:${dk}`, dealId, modules: {} };

      const mk = detailId || '__deal__';
      const detailLabel = detailId ? `Detail ${detailId.slice(0, 8)}…` : null;
      if (!byDeal[dk].modules[mk])
        byDeal[dk].modules[mk] = { label: detailLabel, typeLabel: null, key: `m:${dk}:${mk}`, dealId, detailId, folders: {} };

      const fp = doc.folderPath || '';
      if (!byDeal[dk].modules[mk].folders[fp])
        byDeal[dk].modules[mk].folders[fp] = { label: fp || '— No folder —', key: `f:${dk}:${mk}:${fp}`, dealId, detailId, folderPath: fp, docs: [] };
      byDeal[dk].modules[mk].folders[fp].docs.push(doc);
    }

    if (!search) {
      const extra = [
        ...(dealFilter ? filterDealFolders.map(f => ({ dealId: dealFilter, path: f.path })) : []),
        ...createdFolders,
      ];
      for (const { dealId, path } of extra) {
        if (!dealId || !path || (dealFilter && dealId !== dealFilter)) continue;
        const lower = path.toLowerCase();
        const existing = byDeal[dealId];
        // Already shown, or shown through a sub-folder that holds documents.
        const shown = existing && Object.values(existing.modules).some(m =>
          Object.keys(m.folders).some(fp => fp.toLowerCase() === lower || fp.toLowerCase().startsWith(lower + '/')));
        if (shown) continue;
        if (!byDeal[dealId]) byDeal[dealId] = { label: dealMap[dealId]?.name || 'Unknown Deal', key: `d:${dealId}`, dealId, modules: {} };
        const mods = byDeal[dealId].modules;
        if (!mods.__deal__) mods.__deal__ = { label: null, typeLabel: null, key: `m:${dealId}:__deal__`, dealId, detailId: null, folders: {} };
        mods.__deal__.folders[path] = { label: path, key: `f:${dealId}:__deal__:${path}`, dealId, detailId: null, folderPath: path, docs: [] };
      }
    }

    return Object.values(byDeal)
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(deal => ({
        ...deal,
        modules: Object.values(deal.modules)
          .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
          .map(mod => ({ ...mod, folders: Object.values(mod.folders).sort((a, b) => a.label.localeCompare(b.label)) }))
      }));
  })();

  // All collapsible node keys (for collapse-all / expand-all)
  const allTreeKeys = docTree.flatMap(deal => [
    deal.key,
    ...deal.modules.flatMap(mod => [mod.key, ...mod.folders.map(f => f.key)])
  ]);

  const toggleNode = (key) => setCollapsedFolders(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
  });

  // "Zip" on a deal / detail / folder row: the server bundles that scope's
  // documents and hands back a download. Deal and detail rows go by scope so a
  // deal with hundreds of files still works; a folder row sends its exact file
  // list, since a folder path can repeat across a deal's details.
  const bundleNode = async (key, scope) => {
    setBundling(key);
    try {
      const res = await downloadDocumentsZip(scope);
      if (res?.skipped?.length) toast.info(describeBundle(res));
    } catch (e) { toast.error('Download failed: ' + e.message); }
    finally { setBundling(null); }
  };

  const handleDocDelete = (row) => {
    setConfirmDel({
      title: 'Delete document',
      message: `Delete "${row.fileName}"? It is removed from Harvus and from SharePoint; the stored file is kept, so an administrator can undo this.`,
      onConfirm: async () => {
        setDeleting(row.documentID);
        try {
          const res = await api.del(`documents/${row.documentID}`, { timeout: 60000 });
          if (res?.sharePoint === 'failed') toast.error(`Deleted "${row.fileName}", but it could not be removed from SharePoint: ${res.error || 'unknown error'}`);
          setSelected(prev => { if (!prev.has(row.documentID)) return prev; const n = new Set(prev); n.delete(row.documentID); return n; });
          reload();
        }
        catch (e) { toast.error(e.message); }
        finally { setDeleting(null); }
      },
    });
  };

  // ── Bulk selection ──
  // Selection is by documentID and survives a re-render, but not a reload: a
  // list fetched again is a different list, so anything ticked is dropped with
  // it rather than left pointing at rows that may no longer be on screen.
  const visibleIds = filtered.map(d => d.documentID);
  const selectedVisible = visibleIds.filter(id => selected.has(id));
  const toggleSelected = (id) => setSelected(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  // Ticking a deal / detail / folder row selects every file under it; ticking
  // it again when they are all selected clears them.
  const toggleMany = (ids) => setSelected(prev => {
    const next = new Set(prev);
    const all = ids.length > 0 && ids.every(id => next.has(id));
    for (const id of ids) all ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const reloadAndClear = () => { clearSelection(); reload(); };

  const describeDeleteOutcome = (res) => {
    const parts = [`Deleted ${res.deleted} document${res.deleted === 1 ? '' : 's'}`];
    if (res.sharePointRemoved) parts.push(`${res.sharePointRemoved} removed from SharePoint`);
    if (res.notFound?.length)  parts.push(`${res.notFound.length} already gone`);
    if (res.failed?.length)    parts.push(`${res.failed.length} failed`);
    return parts.join(' · ');
  };

  const handleBulkDelete = () => {
    const ids = selectedVisible;
    if (!ids.length) return;
    setConfirmDel({
      title: `Delete ${ids.length} document${ids.length === 1 ? '' : 's'}?`,
      message: `${ids.length === 1 ? 'This document' : `These ${ids.length} documents`} will be removed from Harvus and from SharePoint. The stored file is kept, so this can be undone by an administrator.`,
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          // The route caps one request at 500 ids (each is a Graph call, run
          // inline), and "select all" on a deal is routinely more than that —
          // so the selection is sent in chunks the route accepts and the
          // outcomes are merged (utils/bulkDelete.js). One Graph call per
          // document server-side, so each request outlives the 10s client
          // default on any real batch.
          const { outcome: res, error, unsent } = await deleteInChunks(ids,
            chunk => api.post('documents/bulk-delete', { documentIds: chunk }, { timeout: 300000 }));
          if (error) {
            // Some chunks landed and one did not: say both, never just the
            // error over a delete that partly happened.
            toast.error(`${res.deleted ? describeDeleteOutcome(res) + '. ' : ''}Delete failed: ${error.message}`
              + (unsent ? ` (${unsent} not attempted)` : ''));
          } else if (res.sharePointFailed?.length) {
            toast.error(`${describeDeleteOutcome(res)}. ${res.sharePointFailed.length} could not be removed from SharePoint: `
              + res.sharePointFailed.slice(0, 3).map(f => f.fileName).join(', ')
              + (res.sharePointFailed.length > 3 ? '…' : ''));
          } else if (res.failed?.length) {
            toast.error(describeDeleteOutcome(res));
          } else {
            toast.success(describeDeleteOutcome(res));
          }
          reloadAndClear();
        } catch (e) { toast.error('Delete failed: ' + e.message); }
        finally { setBulkDeleting(false); }
      },
    });
  };

  const retryOcr = async (docId) => {
    setRetryingOcr(prev => new Set([...prev, docId]));
    try {
      await api.post(`documents/${docId}/retry-ocr`, {});
      setTimeout(reload, 1500); // give OCR a moment to start, then refresh status
    } catch (e) { toast.error('OCR retry failed: ' + e.message); }
    finally { setRetryingOcr(prev => { const s = new Set(prev); s.delete(docId); return s; }); }
  };

  const docStatus = (row) => {
    if (row.isOCRComplete) return 'ocr';
    if (row.ocrError && row.ocrError !== 'Document Intelligence not configured') return 'failed';
    return 'uploaded';
  };

  // Upload modal helpers
  const selectedDetail    = uploadLinkedItem;
  const uploadDetailType  = selectedDetail?.detailType || null;
  // The chosen deal's LIVE folder structure — the templates plus whatever the
  // deal actually has in SharePoint — rather than a fixed list per module type.
  const {
    folders: uploadFolders, tree: uploadFolderTree, loading: uploadFoldersLoading,
    error: uploadFoldersError, createFolder: createUploadFolder,
    sharePointAvailable: uploadSharePointAvailable,
  } = useDocumentFolders({
    dealId:       uploadDealId || null,
    dealDetailId: uploadLinkedItem?.dealDetailID || null,
    detailType:   uploadDetailType,
    enabled:      !!uploadDealId,
  });
  const uploadFolderDef   = uploadFolders.find(f => f.path === uploadFolder);
  const uploadRequiresYear  = uploadFolderDef?.requiresYear || false;
  const effectiveUploadFolder = uploadRequiresYear && uploadTaxYear
    ? `${uploadFolder}/${uploadTaxYear}`
    : uploadFolder;

  const resetUpload = () => {
    setShowUpload(false); setUploadFiles(null); setUploadErr(null);
    setUploadDealId(''); setUploadLinkedItem(null); setUploadDescription('');
    setUploadFolder(''); setUploadTaxYear(''); setUploadDealDetails([]);
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: B.text }}>Documents</div>
        <Btn onClick={() => setShowUpload(true)}>+ Upload Document</Btn>
      </div>

      {/* Upload modal */}
      {showUpload && (
        <Modal title="Upload Document" onClose={resetUpload}>
          {/* Step 1: Deal (required) */}
          <Select label="Deal *" value={uploadDealId}
            onChange={async v => {
              setUploadDealId(v); setUploadLinkedItem(null); setUploadFolder(''); setUploadTaxYear(''); setUploadDealDetails([]);
              if (v) {
                try { const d = await api.get(`deals/${v}`); setUploadDealDetails(d.details || []); } catch {}
              }
            }}
            options={[{ value: '', label: '— Select a deal —' }, ...(allDeals || []).map(d => ({ value: d.dealID, label: d.name }))]}
          />

          {/* Step 2: DealDetail (optional) */}
          {uploadDealId && uploadDealDetails.length > 0 && (
            <div style={{ marginTop: 10, marginBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: B.textMd, marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Deal Detail (optional)</div>
              <select
                value={uploadLinkedItem?.dealDetailID || ''}
                onChange={e => {
                  if (!e.target.value) { setUploadLinkedItem(null); setUploadFolder(''); setUploadTaxYear(''); return; }
                  const detail = uploadDealDetails.find(d => d.dealDetailID === e.target.value);
                  setUploadLinkedItem(detail || null);
                  setUploadFolder(''); setUploadTaxYear('');
                }}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${B.border}`, fontSize: 13, fontFamily: 'inherit', background: B.white, color: B.text }}>
                <option value="">— Attach to the deal (no specific detail) —</option>
                {uploadDealDetails.map(d => (
                  <option key={d.dealDetailID} value={d.dealDetailID}>{d.detailType} Detail — {d.dealDetailID.slice(0, 8)}…</option>
                ))}
              </select>
            </div>
          )}

          {/* Step 3: Folder tree — optional; unset means "file it for me" */}
          {uploadDealId && (
            <div style={{ marginTop: 12 }}>
              <FolderPicker
                label="Folder (optional)"
                hint="Leave this alone and each file is filed for you — pick one only to override that."
                tree={uploadFolderTree}
                folders={uploadFolders}
                loading={uploadFoldersLoading}
                error={uploadFoldersError}
                selected={uploadFolder}
                onSelect={setUploadFolder}
                taxYear={uploadTaxYear}
                onTaxYear={setUploadTaxYear}
                onCreateFolder={async (p) => {
                  const f = await createUploadFolder(p);
                  if (f?.path && uploadDealId) setCreatedFolders(prev => [...prev, { dealId: uploadDealId, path: f.path }]);
                  return f;
                }}
                sharePointAvailable={uploadSharePointAvailable}
              />
            </div>
          )}

          <Input label="Description (optional)" value={uploadDescription}
            onChange={v => setUploadDescription(v)} />

          <div {...clickable(() => uploadFileRef.current?.click())} aria-label="Choose files to upload"
            style={{ border: `2px dashed ${uploadFiles ? B.tealDk : B.border}`, borderRadius: 8,
              padding: "20px", textAlign: "center", cursor: "pointer", margin: "12px 0", background: B.surfaceMuted }}>
            <input ref={uploadFileRef} type="file" multiple style={{ display: "none" }}
              onChange={e => setUploadFiles(collectEntriesFromInputFiles(e.target.files).map(x => x.file))} />
            <input ref={uploadFolderRef} type="file" webkitdirectory="" style={{ display: "none" }}
              onChange={e => setUploadFiles(collectEntriesFromInputFiles(e.target.files).map(x => x.file))} />
            <div style={{ fontSize: 13, color: uploadFiles ? B.tealDk : B.text, fontWeight: uploadFiles ? 600 : 400 }}>
              {uploadFiles
                ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name={uploadFiles.length === 1 ? "attach" : "folder"} size={14} />{uploadFiles.length === 1 ? uploadFiles[0].name : `${uploadFiles.length} files selected`}</span>
                : "Click to choose files"}
            </div>
            <div style={{ fontSize: 11, color: B.textLt, marginTop: 4 }}>
              <span onClick={e => { e.stopPropagation(); uploadFolderRef.current?.click(); }}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}>Browse folder</span>
            </div>
          </div>
          {uploadErr && (
            <div style={{ color: B.red, fontSize: 12, marginBottom: 8, padding: "8px 12px", background: B.red + "10", borderRadius: 6, border: `1px solid ${B.red}33`, display: "flex", alignItems: "center", gap: 5 }}>
              <Icon name="warning" size={13} /> {uploadErr}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={resetUpload}>Cancel</Btn>
            <Btn disabled={!uploadFiles || uploadingDoc} onClick={async () => {
              if (!uploadFiles) return;
              if (!uploadDealId) { setUploadErr('Please select a deal.'); return; }
              // A folder is optional — anything uploaded without one is
              // auto-filed server-side. The tax year is still required, but
              // only when a folder that asks for one was actually chosen.
              if (uploadRequiresYear && !uploadTaxYear) {
                setUploadErr('Please enter the Tax Year for Property Taxes documents.');
                return;
              }
              setUploadingDoc(true); setUploadErr(null);
              try {
                const unfiled = [];
                // A file the server refuses as incomplete is ONE file's
                // problem, not the batch's — see the catch below.
                const rejected = [];
                for (const file of uploadFiles) {
                  const result = await api.post("document-upload-url", {
                    fileName:     file.name,
                    fileType:     file.name.split(".").pop().toLowerCase(),
                    dealId:       uploadDealId,
                    dealDetailId: uploadLinkedItem?.dealDetailID || null,
                    description:  uploadDescription || "",
                    folderPath:   effectiveUploadFolder || null,
                  });
                  if (!result?.sasUrl) throw new Error("No upload URL returned — check StorageConnectionString is configured in Azure Function App settings");
                  const putRes = await fetch(result.sasUrl, {
                    method: "PUT",
                    headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/octet-stream" },
                    body: file,
                  });
                  if (!putRes.ok) throw new Error(`Blob upload failed (${putRes.status}) — check storage permissions`);
                  // This page used to stop at the blob PUT and never confirm the
                  // upload, so the file size went unrecorded and the SharePoint
                  // sync never ran at all. Confirm it like every other client.
                  try {
                    await api.post(`documents/${result.documentId}/confirm-upload`, {
                      fileSizeBytes: file.size,
                      versionId:     result.versionId,
                      // We follow up with one batched auto-file call below.
                      deferAutoFile: !effectiveUploadFolder,
                    });
                  } catch (e) {
                    // confirm-upload verifies that what landed in Blob Storage
                    // is what the browser said it was sending, and discards the
                    // document when it is not. That is a fact about THIS file
                    // (usually one held open elsewhere and re-saved mid-read),
                    // so the rest of the batch carries on. Anything else still
                    // aborts — it will repeat identically on every file left.
                    if (e?.body?.discarded) { rejected.push({ name: file.name, reason: e.message }); continue; }
                    throw e;
                  }
                  if (!effectiveUploadFolder) unfiled.push(result.documentId);
                }

                // One batched call files everything that arrived without a
                // folder (and completes the syncs deferred above).
                if (unfiled.length) {
                  try {
                    await api.post('documents/auto-file', {
                      dealId:       uploadDealId,
                      dealDetailId: uploadLinkedItem?.dealDetailID || null,
                      documentIds:  unfiled,
                    }, { timeout: 120000 });
                  } catch (e) {
                    // Uploaded and safe — they'd just be sitting at the deal root.
                    console.warn('[Documents] auto-file failed:', e.message); // eslint-disable-line no-console
                  }
                }
                // Discarded files keep the modal open and name themselves —
                // closing it would report a clean upload for a batch that was
                // not one, leaving the person to work out which files to redo.
                if (rejected.length) {
                  reload();
                  setUploadErr(rejected.length === 1
                    ? rejected[0].reason
                    : `${rejected.length} files did not upload completely and were discarded: `
                      + rejected.map(r => r.name).join(', ')
                      + '. Close them in any program that has them open, then try again.');
                  return;
                }
                resetUpload();
                reload();
              } catch (e) { setUploadErr(e.message); }
              finally { setUploadingDoc(false); }
            }}>{uploadingDoc ? "Uploading…" : "Upload"}</Btn>
          </div>
        </Modal>
      )}

      {/* Filter bar */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input value={searchInput} onChange={e => handleSearchChange(e.target.value)}
            placeholder="Search documents…"
            style={{ flex: "1 1 220px", padding: "8px 12px", border: `1px solid ${B.border}`, borderRadius: 6, fontSize: 13, fontFamily: "inherit" }} />
          <select value={dealFilter} onChange={e => setDealFilter(e.target.value)} aria-label="Filter by deal"
            style={{ flex: "0 1 260px", minWidth: 160, padding: "8px 10px", border: `1px solid ${dealFilter ? B.navy : B.border}`, borderRadius: 6, fontSize: 13, fontFamily: "inherit", background: B.white, color: B.text }}>
            <option value="">All deals</option>
            {(allDeals || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(d => (
              <option key={d.dealID} value={d.dealID}>{d.name}</option>
            ))}
          </select>
          {!search && filtered.length > 0 && (
            <Btn size="sm" variant="ghost"
              onClick={() => collapsedFolders.size ? setCollapsedFolders(new Set()) : setCollapsedFolders(new Set(allTreeKeys))}>
              {collapsedFolders.size ? "Expand all" : "Collapse all"}
            </Btn>
          )}
          {(searchInput || dealFilter) && (
            <Btn size="sm" variant="ghost" onClick={() => { setSearch(""); setSearchInput(""); setDealFilter(""); }}><Icon name="close" size={13} /> Clear</Btn>
          )}
        </div>
        <div style={{ fontSize: 12, color: B.textLt, marginTop: 6 }}>
          {filtered.length} document{filtered.length !== 1 ? "s" : ""}
          {search ? ` matching "${search}"` : ""}
          {dealFilter ? ` on ${(allDeals || []).find(d => d.dealID === dealFilter)?.name || 'this deal'}` : ""}
        </div>
        {/* Selection toolbar — only while something is ticked */}
        {selectedVisible.length > 0 && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10, padding: "8px 12px",
            background: B.navy, borderRadius: 6, color: '#fff', fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>{selectedVisible.length} selected</span>
            <Btn size="sm" variant="ghost" onClick={() => toggleMany(visibleIds)} style={toolbarBtnStyle}>
              {selectedVisible.length === visibleIds.length ? 'Deselect all' : `Select all ${visibleIds.length}`}
            </Btn>
            <span style={{ flex: 1 }} />
            <Btn size="sm" variant="ghost" onClick={clearSelection} style={toolbarBtnStyle}>Clear</Btn>
            <Btn size="sm" variant="danger" disabled={bulkDeleting} onClick={handleBulkDelete}>
              <Icon name="trash" size={13} /> {bulkDeleting ? 'Deleting…' : `Delete ${selectedVisible.length}`}
            </Btn>
          </div>
        )}
      </Card>

      {/* File browser */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {/* Column header */}
        <div style={{ display: "grid", gridTemplateColumns: "26px minmax(0,1fr) 70px 100px 120px 70px 110px",
          padding: "8px 16px", background: B.surfaceMuted, borderBottom: `1px solid ${B.border}`,
          fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.4, textTransform: "uppercase" }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {visibleIds.length > 0 && (
              <Tick label={selectedVisible.length === visibleIds.length ? 'Deselect all documents' : 'Select all documents'}
                checked={selectedVisible.length > 0 && selectedVisible.length === visibleIds.length}
                partial={selectedVisible.length > 0}
                onChange={() => toggleMany(visibleIds)} />
            )}
          </div>
          <div>Name</div>
          <div>Status</div>
          <div>Uploaded</div>
          <div>By</div>
          <div>Size</div>
          <div></div>
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: B.textLt }}>No documents found.</div>
        )}

        {/* FILE GRID — shared column layout for all file rows */}
        {/* When searching: flat results with breadcrumb path */}
        {search ? filtered.map(row => {
          const st = docStatus(row);
          return (
            <div key={row.documentID} style={{ display: "grid", gridTemplateColumns: "26px minmax(0,1fr) 70px 100px 120px 70px 110px",
              padding: "9px 16px", borderBottom: `1px solid ${B.border}`, alignItems: "center", background: selected.has(row.documentID) ? B.surfaceMuted : B.white }}>
              <Tick label={`Select ${row.fileName}`} checked={selected.has(row.documentID)} onChange={() => toggleSelected(row.documentID)} />
              <div style={{ minWidth: 0 }}>
                <div {...clickable(() => openPreview(row))} style={{ fontWeight: 600, color: B.navy, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.fileName}</div>
                {row.folderPath && <div style={{ fontSize: 11, color: B.textLt, marginTop: 1, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="folder" size={11} /> {row.folderPath}</div>}
              </div>
              <StatusCell st={st} row={row} retryingOcr={retryingOcr} retryOcr={retryOcr} />
              <div style={{ fontSize: 12, color: B.textMd }}>{fmt.date(row.uploadedUTC)}</div>
              <div style={{ fontSize: 12, color: B.textMd, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.uploadedByName}</div>
              <div style={{ fontSize: 12, color: B.textMd }}>{fmt.fileSize(row.fileSizeBytes)}</div>
              <DocActions row={row} viewing={viewing} setViewing={setViewing} downloading={downloading} download={download} deleting={deleting} setDeleting={setDeleting} openPreview={openPreview} reload={reload} api={api} setViewerState={setViewerState} onDelete={handleDocDelete} />
            </div>
          );
        }) : (
          /* 3-level tree: deal → module → folder → files */
          docTree.map(deal => {
            const dealCollapsed = collapsedFolders.has(deal.key);
            const dealFileCount = deal.modules.reduce((s, m) => s + m.folders.reduce((s2, f) => s2 + f.docs.length, 0), 0);
            const dealIds = deal.modules.flatMap(m => m.folders.flatMap(f => f.docs.map(d => d.documentID)));
            const dealSel = dealIds.filter(id => selected.has(id)).length;
            return (
              <div key={deal.key}>
                {/* DEAL ROW */}
                <div {...clickable(() => toggleNode(deal.key))} aria-expanded={!collapsedFolders.has(deal.key)}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px",
                    background: B.navy, borderBottom: `1px solid ${B.navyDk}`, cursor: "pointer", userSelect: "none" }}>
                  <Tick tone="dark" label={`Select every document under ${deal.label}`}
                    checked={dealSel > 0 && dealSel === dealIds.length} partial={dealSel > 0}
                    onChange={() => toggleMany(dealIds)} />
                  <span style={{ color: '#fff', display: 'flex' }}><Icon name={dealCollapsed ? 'chevronRight' : 'chevronDown'} size={14} /></span>
                  <span style={{ color: B.teal, display: 'flex' }}><Icon name="folder" size={15} /></span>
                  <span style={{ fontWeight: 700, color: '#fff', fontSize: 13, flex: 1 }}>{deal.label}</span>
                  <span style={{ fontSize: 11, color: B.teal }}>{dealFileCount} file{dealFileCount !== 1 ? 's' : ''}</span>
                  {deal.dealId && (
                    <BundleBtn
                      label={`Download all ${dealFileCount} document${dealFileCount !== 1 ? 's' : ''} for ${deal.label}`}
                      busy={bundling === deal.key}
                      disabled={!dealFileCount || (bundling && bundling !== deal.key)}
                      onClick={() => bundleNode(deal.key, { dealId: deal.dealId })}
                    />
                  )}
                </div>

                {!dealCollapsed && deal.modules.map(mod => {
                  const modCollapsed = collapsedFolders.has(mod.key);
                  const modFileCount = mod.folders.reduce((s, f) => s + f.docs.length, 0);
                  return (
                    <div key={mod.key}>
                      {/* MODULE ROW (only when doc belongs to a sub-record, not deal-level) */}
                      {mod.label && (
                        <div {...clickable(() => toggleNode(mod.key))} aria-expanded={!collapsedFolders.has(mod.key)}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px 8px 28px",
                            background: B.navyLt, borderBottom: `1px solid ${B.navyDk}`, cursor: "pointer", userSelect: "none" }}>
                          <span style={{ color: B.tealLt, display: 'flex' }}><Icon name={modCollapsed ? 'chevronRight' : 'chevronDown'} size={13} /></span>
                          <span style={{ color: B.tealLt, display: 'flex' }}><Icon name="folder" size={14} /></span>
                          <span style={{ fontWeight: 600, color: '#fff', fontSize: 13, flex: 1 }}>{mod.label}</span>
                          {mod.typeLabel && <span style={{ fontSize: 10, color: B.teal, background: 'rgba(0,0,0,0.2)', borderRadius: 3, padding: '1px 5px' }}>{mod.typeLabel}</span>}
                          <span style={{ fontSize: 11, color: B.teal, marginLeft: 6 }}>{modFileCount} file{modFileCount !== 1 ? 's' : ''}</span>
                          {mod.detailId && (
                            <BundleBtn
                              label={`Download all ${modFileCount} document${modFileCount !== 1 ? 's' : ''} for this deal detail`}
                              busy={bundling === mod.key}
                              disabled={!modFileCount || (bundling && bundling !== mod.key)}
                              onClick={() => bundleNode(mod.key, { dealId: mod.dealId, dealDetailId: mod.detailId })}
                            />
                          )}
                        </div>
                      )}

                      {/* FOLDER ROWS */}
                      {(!mod.label || !modCollapsed) && mod.folders.map(folder => {
                        const folderCollapsed = collapsedFolders.has(folder.key);
                        const indent = mod.label ? 40 : 28;
                        const folderIds = folder.docs.map(d => d.documentID);
                        const folderSel = folderIds.filter(id => selected.has(id)).length;
                        return (
                          <div key={folder.key}>
                            <div {...clickable(() => toggleNode(folder.key))} aria-expanded={!collapsedFolders.has(folder.key)}
                              style={{ display: "flex", alignItems: "center", gap: 8,
                                padding: `7px 16px 7px ${indent}px`,
                                background: B.surfaceMuted, borderBottom: `1px solid ${B.border}`,
                                cursor: "pointer", userSelect: "none" }}>
                              <Tick label={`Select every document in ${folder.label}`}
                                checked={folderSel > 0 && folderSel === folderIds.length} partial={folderSel > 0}
                                onChange={() => toggleMany(folderIds)} />
                              <span style={{ color: B.textMd, display: 'flex' }}><Icon name={folderCollapsed ? 'chevronRight' : 'chevronDown'} size={13} /></span>
                              <span style={{ color: B.tealDk, display: 'flex' }}><Icon name="folder" size={14} /></span>
                              <span style={{ fontWeight: 600, color: B.navy, fontSize: 13, flex: 1 }}>{folder.label}</span>
                              <span style={{ fontSize: 11, color: B.textLt }}>{folder.docs.length} file{folder.docs.length !== 1 ? 's' : ''}</span>
                              {(folder.dealId || folder.detailId) && (
                                <BundleBtn
                                  tone="light"
                                  label={`Download the ${folder.docs.length} document${folder.docs.length !== 1 ? 's' : ''} in ${folder.label}`}
                                  busy={bundling === folder.key}
                                  disabled={!folder.docs.length || (bundling && bundling !== folder.key)}
                                  onClick={() => bundleNode(folder.key, {
                                    dealId: folder.dealId,
                                    dealDetailId: folder.detailId,
                                    documentIds: folder.docs.map(d => d.documentID),
                                  })}
                                />
                              )}
                            </div>

                            {/* FILE ROWS */}
                            {!folderCollapsed && folder.docs.map(row => {
                              const st = docStatus(row);
                              const fileIndent = mod.label ? 56 : 44;
                              return (
                                <div key={row.documentID} style={{ display: "grid", gridTemplateColumns: "26px minmax(0,1fr) 70px 100px 120px 70px 110px",
                                  padding: `8px 16px 8px ${fileIndent}px`, borderBottom: `1px solid ${B.border}`,
                                  alignItems: "center", background: selected.has(row.documentID) ? B.surfaceMuted : B.white }}>
                                  <Tick label={`Select ${row.fileName}`} checked={selected.has(row.documentID)} onChange={() => toggleSelected(row.documentID)} />
                                  <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ display: "flex", color: B.textMd, flexShrink: 0 }}><Icon name="file" size={14} /></span>
                                    <div style={{ minWidth: 0 }}>
                                      <div {...clickable(() => openPreview(row))} style={{ fontWeight: 600, color: B.navy, cursor: "pointer", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.fileName}</div>
                                      {row.description && <div style={{ fontSize: 11, color: B.textLt }}>{row.description}</div>}
                                    </div>
                                  </div>
                                  <StatusCell st={st} row={row} retryingOcr={retryingOcr} retryOcr={retryOcr} />
                                  <div style={{ fontSize: 12, color: B.textMd }}>{fmt.date(row.uploadedUTC)}</div>
                                  <div style={{ fontSize: 12, color: B.textMd, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.uploadedByName}</div>
                                  <div style={{ fontSize: 12, color: B.textMd }}>{fmt.fileSize(row.fileSizeBytes)}</div>
                                  <DocActions row={row} viewing={viewing} setViewing={setViewing} downloading={downloading} download={download} deleting={deleting} setDeleting={setDeleting} openPreview={openPreview} reload={reload} api={api} setViewerState={setViewerState} onDelete={handleDocDelete} />
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </Card>

      {/* Document detail modal */}
      {preview && (
        <Modal title={preview.fileName} onClose={() => setPreview(null)} width={760}>
          <InfoGrid items={[
            ["Deal",        preview.dealID    ? ((allDeals || []).find(d => d.dealID === preview.dealID)?.name || preview.dealID) : "—"],
            ["Folder",      preview.folderPath || "—"],
            ["Uploaded",    fmt.date(preview.uploadedUTC)],
            ["By",          preview.uploadedByName],
            ["Size",        fmt.fileSize(preview.fileSizeBytes)],
            ["Status",      preview.isOCRComplete ? "OCR indexed" : (preview.ocrError && preview.ocrError !== 'Document Intelligence not configured') ? "OCR Failed" : "Uploaded"],
            ["SharePoint",  preview.sharePointSyncStatus === 'synced' ? "Synced" : preview.sharePointSyncStatus === 'pending' ? "Syncing…" : preview.sharePointSyncStatus === 'error' ? `Error: ${preview.sharePointSyncError || 'unknown'}` : "—"],
          ]} />

          <div style={{ background: B.surfaceMuted, borderRadius: 8, minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${B.border}` }}>
            {preview.fileType === "pdf"
              ? <div style={{ textAlign: "center", padding: 32 }}>
                  <div style={{ display: "flex", justifyContent: "center", color: B.textLt, marginBottom: 8 }}><Icon name="file" size={40} strokeWidth={1.5} /></div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <Btn onClick={() => download(preview)}><Icon name="download" size={14} /> Download PDF</Btn>
                    {preview.sharePointItemID && (
                      <Btn variant="secondary" onClick={async () => {
                        try {
                          const res = await api.get(`documents/${preview.documentID}/edit-url`);
                          if (res?.editUrl) window.open(res.editUrl, "_blank");
                        } catch (e) { toast.error("Could not open in Office: " + e.message); }
                      }}>Open in Office</Btn>
                    )}
                  </div>
                </div>
              : <div style={{ textAlign: "center", padding: 32 }}>
                  <div style={{ display: "flex", justifyContent: "center", color: B.textLt, marginBottom: 8 }}><Icon name="attach" size={40} strokeWidth={1.5} /></div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <Btn onClick={() => download(preview)}><Icon name="download" size={14} /> Download</Btn>
                    {preview.sharePointItemID && (
                      <Btn variant="secondary" onClick={async () => {
                        try {
                          const res = await api.get(`documents/${preview.documentID}/edit-url`);
                          if (res?.editUrl) window.open(res.editUrl, "_blank");
                        } catch (e) { toast.error("Could not open in Office: " + e.message); }
                      }}>Open in Office</Btn>
                    )}
                  </div>
                </div>
            }
          </div>

          {preview.sharePointSyncStatus === 'error' && (
            <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12, color: B.red, background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>SharePoint sync failed: {preview.sharePointSyncError || 'unknown error'}</span>
              <Btn size="sm" variant="secondary" onClick={async () => {
                try {
                  await api.post(`documents/${preview.documentID}/retry-sync`, {});
                  setPreview(p => ({ ...p, sharePointSyncStatus: 'pending', sharePointSyncError: null }));
                  setTimeout(reload, 3000);
                } catch (e) { toast.error('Retry failed: ' + e.message); }
              }}>Retry sync</Btn>
            </div>
          )}
          {preview.ocrError && (
            <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12, color: B.red, background: '#fef2f2', border: '1px solid #fecaca' }}>
              OCR Error: {preview.ocrError}
            </div>
          )}
        </Modal>
      )}

      <DocViewerModal viewerState={viewerState} onClose={() => setViewerState(null)} />

      {confirmDel && (
        <Modal title={confirmDel.title || 'Confirm Delete'} onClose={() => setConfirmDel(null)}>
          <div style={{ fontSize: 14, color: B.text, marginBottom: 20 }}>{confirmDel.message}</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={() => { const fn = confirmDel.onConfirm; setConfirmDel(null); fn(); }}>Delete</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};
